import DigiobcanApi, {
  type IssueStatement,
} from '../../../../api/DigiobcanApi';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';
import { useM } from '../../../../lib/query';
import { useLogger } from '../../../../providers/LoggerProvider';
import { __, toastError, toastSuccess } from '../../../../utils';
import type { PendingFile } from './IssueStatementSection';
import {
  Button,
  Stack,
  TextField,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  /** Zverejnené vyjadrenie, ktoré sa upravuje; `null` = dialóg je zavretý. */
  statement: IssueStatement | null;
  /**
   * Platí prihlásenému v kategórii nálezu `requires_approval`? Potom úprava
   * čaká na schválenie a občan dovtedy vidí doterajšie znenie - tlačidlo to
   * musí povedať, inak by úradník čakal, že sa zmena prejaví hneď.
   */
  requiresApproval: boolean;
  onClose: () => void;
  onDone: () => Promise<unknown>;
};

/**
 * Úprava zverejneného vyjadrenia (moj-presov#18) - text aj prílohy naraz.
 *
 * Prílohy sú **jeden zoznam**: doterajšie s „Odobrať" (odoberie ich až
 * zverejnená úprava, v histórii zostanú) a novo vybrané s „Odstrániť".
 * Odosiela sa ako pri novom vyjadrení: najprv úprava, potom súbory k nej -
 * keď upload zlyhá, úprava existuje bez nich a schvaľovateľ to uvidí.
 */
const StatementRevisionDialog: React.FC<Props> = ({
  statement,
  requiresApproval,
  onClose,
  onDone,
}) => {
  const { logger } = useLogger();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [text, setText] = React.useState('');
  const [removed, setRemoved] = React.useState<string[]>([]);
  const [files, setFiles] = React.useState<PendingFile[]>([]);

  // Každé otvorenie začína od zverejneného znenia - rozpísaná úprava iného
  // vyjadrenia k tomuto nepatrí.
  React.useEffect(() => {
    setText(statement?.text ?? '');
    setRemoved([]);
    setFiles([]);
  }, [statement]);

  const trimmed = text.trim();
  const changed =
    !!statement &&
    (trimmed !== statement.text || removed.length > 0 || files.length > 0);

  const mutation = useM(
    async () => {
      if (!statement) return false;

      const res = await DigiobcanApi.adminReviseStatement(
        statement.statement_id,
        {},
        {
          text: trimmed,
          ...(removed.length && { removed_media_ids: removed }),
        },
      );

      if (files.length) {
        await DigiobcanApi.adminAddStatementRevisionMedia(res.revision_id, {}, {
          media: files.map((f) => f.file),
        } as never);
      }

      await onDone();
      return !!res.published;
    },
    {
      onSuccess: (published) => {
        toastSuccess(
          published
            ? __('Vyjadrenie bolo upravené.')
            : __(
                'Úprava bola odoslaná na schválenie. Občan dovtedy vidí doterajšie znenie.',
              ),
        );
        onClose();
      },
      onError: (error) => {
        logger.error('Failed to revise statement', {
          action: 'statementRevise',
          error,
          data: { statementId: statement?.statement_id },
        });

        const name = error.details?.name;
        if (
          name === 'STATEMENT_REVISION_PENDING' ||
          name === 'STATEMENT_NOT_PUBLISHED' ||
          name === 'STATEMENT_MEDIA_NOT_FOUND'
        ) {
          toastError(
            __(
              'S vyjadrením už medzitým niekto niečo urobil. Obnovte stránku.',
            ),
          );
        } else if (name === 'FORBIDDEN_ISSUE_STATE_UPDATE') {
          toastError(__('Vyjadrenia v tejto kategórii neupravujete.'));
        } else if (name === 'REPORT_ARCHIVED') {
          toastError(__('Podnet je archivovaný.'));
        } else {
          toastError(__('Úpravu sa nepodarilo uložiť.'));
        }
      },
    },
  );

  const toggleRemoved = (mediaId: string) =>
    setRemoved((current) =>
      current.includes(mediaId)
        ? current.filter((id) => id !== mediaId)
        : [...current, mediaId],
    );

  return (
    <ConfirmationDialog
      open={!!statement}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
      title={
        statement?.final
          ? __('Upraviť finálne vyjadrenie')
          : __('Upraviť vyjadrenie')
      }
      confirmLabel={
        requiresApproval ? __('Odoslať na schválenie') : __('Uložiť úpravu')
      }
      confirmDisabled={!changed || !trimmed}
      isLoading={mutation.isPending}
      onConfirm={() => mutation.mutate()}
    >
      <Stack gap={1.5}>
        <Typography variant='body2' color='text.secondary'>
          {requiresApproval
            ? __(
                'Úpravu pred zverejnením schváli schvaľovateľ. Dovtedy občan vidí doterajšie znenie.',
              )
            : __(
                'Občan uvidí nové znenie so štítkom „upravené" a dostane o úprave správu. Doterajšie znenie zostane v histórii.',
              )}
        </Typography>

        <TextField
          multiline
          minRows={4}
          fullWidth
          value={text}
          onChange={(event) => setText(event.target.value)}
          inputProps={{ maxLength: 4096 }}
        />

        <input
          ref={inputRef}
          type='file'
          multiple
          hidden
          onChange={(event) => {
            const picked = Array.from(event.target.files ?? []);
            setFiles((current) => [
              ...current,
              ...picked.map((file) => ({
                id: `${file.name}-${file.size}-${file.lastModified}`,
                file,
              })),
            ]);
            event.target.value = '';
          }}
        />

        <Stack gap={0.75}>
          <Stack direction='row' alignItems='center' gap={1.25}>
            <Typography variant='caption' fontWeight={500}>
              {__('Prílohy vyjadrenia')}
            </Typography>
            {/* `outline` z dizajn systému ako pri novom vyjadrení: červená
            patrí potvrdzovaciemu tlačidlu dialógu, nie výberu súboru. */}
            <Button
              variant='outline'
              size='small'
              sx={{ ml: 'auto' }}
              onClick={() => inputRef.current?.click()}
            >
              {__('Nahrať prílohu')}
            </Button>
          </Stack>

          {statement?.media.map((media) => {
            const gone = removed.includes(media.media_id);

            return (
              <Stack
                key={media.media_id}
                direction='row'
                alignItems='center'
                gap={1}
              >
                <Typography
                  variant='body2'
                  noWrap
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    color: gone ? 'text.disabled' : 'text.primary',
                    textDecoration: gone ? 'line-through' : 'none',
                  }}
                >
                  {media.original_filename ?? media.media_id}
                </Typography>
                <Button
                  variant='text'
                  size='small'
                  onClick={() => toggleRemoved(media.media_id)}
                >
                  {gone ? __('Ponechať') : __('Odobrať')}
                </Button>
              </Stack>
            );
          })}

          {files.map((row) => (
            <Stack key={row.id} direction='row' alignItems='center' gap={1}>
              <Typography variant='body2' noWrap sx={{ flex: 1, minWidth: 0 }}>
                {row.file.name}
              </Typography>
              <Typography variant='caption' color='success.dark'>
                {__('nová')}
              </Typography>
              <Button
                variant='text'
                size='small'
                onClick={() =>
                  setFiles((current) => current.filter((f) => f.id !== row.id))
                }
              >
                {__('Odstrániť')}
              </Button>
            </Stack>
          ))}

          {!statement?.media.length && !files.length && (
            <Typography variant='caption' color='text.secondary'>
              {__('žiadne')}
            </Typography>
          )}
        </Stack>
      </Stack>
    </ConfirmationDialog>
  );
};

export default StatementRevisionDialog;
